import { useState, useEffect, useRef } from 'react'
import type { CVData } from '@/types'
import { Button } from '@/components/ui/button'
import { Loader2, Download, Save } from 'lucide-react'

interface HTMLCVPreviewProps {
  cv: CVData
  onSave?: () => void
  isSaving?: boolean
  cvName?: string
}

// A4 dimensions in pixels at 96 DPI
const A4_WIDTH = 794
const A4_HEIGHT = 1123

// Margins in pixels (1cm = 37.795px at 96 DPI)
const MARGIN_TOP = 60 // 1.6cm ≈ 60px
const MARGIN_BOTTOM = 96 // 2.54cm ≈ 96px
const MARGIN_LEFT = 96 // 2.54cm ≈ 96px
const MARGIN_RIGHT = 96 // 2.54cm ≈ 96px
const CONTENT_HEIGHT_PER_PAGE = A4_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM // ~967px

interface SectionInfo {
  html: string
  height: number
}

const HTMLCVPreview = ({ cv, onSave, isSaving = false, cvName }: HTMLCVPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measurementRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [pages, setPages] = useState<SectionInfo[][]>([[]])
  const [isDownloading, setIsDownloading] = useState(false)

  // Auto-scale to fit container while maintaining A4 aspect ratio
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return

      const containerWidth = containerRef.current.clientWidth - 48 // padding
      const newScale = Math.min(1, containerWidth / A4_WIDTH)
      setScale(newScale)
    }

    updateScale()

    const resizeObserver = new ResizeObserver(updateScale)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [])

  // Calculate pages with item-level awareness
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!measurementRef.current) return

      const nameElement = measurementRef.current.querySelector('.name') as HTMLElement | null
      const contactElement = measurementRef.current.querySelector('.contact-info') as HTMLElement | null

      // Get name and contact heights
      const nameHeight = nameElement?.offsetHeight || 0
      const contactHeight = contactElement?.offsetHeight || 0
      const headerHeight = nameHeight + contactHeight + 15 // margin

      // Collect all elements to paginate (section titles and items)
      const elementsToPaginate: Array<{ html: string; height: number; type: 'title' | 'item' | 'content' }> = []

      // Helper to add section title
      const addSectionTitle = (title: string) => {
        const titleElement = measurementRef.current?.querySelector(`[data-section-title="${title}"]`) as HTMLElement | null
        if (titleElement) {
          elementsToPaginate.push({
            html: titleElement.outerHTML,
            height: titleElement.offsetHeight,
            type: 'title'
          })
        }
      }

      // Helper to add items
      const addItems = (selector: string) => {
        const items = measurementRef.current?.querySelectorAll(selector) || []
        items.forEach((item) => {
          elementsToPaginate.push({
            html: (item as HTMLElement).outerHTML,
            height: (item as HTMLElement).offsetHeight,
            type: 'item'
          })
        })
      }

      // Helper to add standalone content (like summary or skills)
      const addStandaloneContent = (selector: string) => {
        const element = measurementRef.current?.querySelector(selector) as HTMLElement | null
        if (element) {
          elementsToPaginate.push({
            html: element.outerHTML,
            height: element.offsetHeight,
            type: 'content'
          })
        }
      }

      // Add summary if exists
      if (cv.summary) {
        addSectionTitle('Professional Summary')
        addStandaloneContent('[data-section="summary"]')
      }

      // Add work experience items
      if (cv.workExperience && cv.workExperience.length > 0) {
        addSectionTitle('Work Experience')
        addItems('.experience-item')
      }

      // Add project items
      if (cv.projects && cv.projects.length > 0) {
        addSectionTitle('Projects')
        addItems('.project-item')
      }

      // Add education items
      if (cv.education && cv.education.length > 0) {
        addSectionTitle('Education')
        addItems('.education-item')
      }

      // Add course items
      if (cv.courses && cv.courses.length > 0) {
        addSectionTitle('Courses & Certifications')
        addItems('.course-item')
      }

      // Add skills
      if (cv.skills && cv.skills.length > 0) {
        addSectionTitle('Skills')
        addStandaloneContent('[data-section="skills"]')
      }

      // Now paginate at item level
      const newPages: SectionInfo[][] = []
      let currentPage: SectionInfo[] = []
      let currentHeight = headerHeight

      elementsToPaginate.forEach((element) => {
        const elementWithMargin = element.height + (element.type === 'item' ? 10 : element.type === 'content' ? 0 : 8)

        if (currentHeight + elementWithMargin <= CONTENT_HEIGHT_PER_PAGE) {
          currentPage.push({ html: element.html, height: element.height })
          currentHeight += elementWithMargin
        } else {
          // Start new page
          if (currentPage.length > 0) {
            newPages.push(currentPage)
          }
          currentPage = [{ html: element.html, height: element.height }]
          currentHeight = headerHeight + elementWithMargin
        }
      })

      // Add last page
      if (currentPage.length > 0) {
        newPages.push(currentPage)
      }

      setPages(newPages)
    }, 150)

    return () => clearTimeout(timer)
  }, [cv])

  // Generate CV HTML content
  const generateHTMLContent = () => {
    const escapeHtml = (text: string): string => {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    }

    return `
      <div class="name">${escapeHtml(cv.personalInfo.name || 'YOUR NAME')}</div>

      <div class="contact-info">
        ${cv.personalInfo.email ? `<span>${escapeHtml(cv.personalInfo.email)}</span>` : ''}
        ${cv.personalInfo.phone ? `<span>| ${escapeHtml(cv.personalInfo.phone)}</span>` : ''}
        ${cv.personalInfo.linkedin ? `<span>| ${escapeHtml(cv.personalInfo.linkedin)}</span>` : ''}
        ${cv.personalInfo.portfolio ? `<span>| ${escapeHtml(cv.personalInfo.portfolio)}</span>` : ''}
      </div>

      ${cv.summary ? `
      <div class="section-title no-line" data-section-title="Professional Summary">Professional Summary</div>
      <div class="summary-content" data-section="summary">${cv.summary}</div>
      ` : ''}

      ${cv.workExperience && cv.workExperience.length > 0 ? `
      <div class="section-title with-line" data-section-title="Work Experience">Work Experience</div>
      ` : ''}

      ${cv.workExperience && cv.workExperience.length > 0 ? cv.workExperience.map(exp => `
        <div class="experience-item">
          <div class="item-header">
            <div class="item-title">${escapeHtml(exp.role)}</div>
            <div class="item-date">${escapeHtml(exp.duration)}</div>
          </div>
          <div class="item-subtitle">${escapeHtml(exp.company)}</div>
          ${exp.description ? `<div class="item-description">${exp.description}</div>` : ''}
        </div>
      `).join('') : ''}

      ${cv.projects && cv.projects.length > 0 ? `
      <div class="section-title with-line" data-section-title="Projects">Projects</div>
      ` : ''}

      ${cv.projects && cv.projects.length > 0 ? cv.projects.map(project => `
        <div class="project-item">
          <div class="item-header">
            <div class="item-title">
              ${project.link ? `<a href="${escapeHtml(project.link)}">${escapeHtml(project.name)}</a>` : escapeHtml(project.name)}
            </div>
            <div class="item-date">${escapeHtml(project.duration)}</div>
          </div>
          ${project.description ? `<div class="item-description">${project.description}</div>` : ''}
        </div>
      `).join('') : ''}

      ${cv.education && cv.education.length > 0 ? `
      <div class="section-title with-line" data-section-title="Education">Education</div>
      ` : ''}

      ${cv.education && cv.education.length > 0 ? cv.education.map(edu => `
        <div class="education-item">
          <div class="item-header">
            <div class="item-title">${escapeHtml(edu.major)}</div>
            <div class="item-date">${escapeHtml(edu.year)}</div>
          </div>
          <div class="item-subtitle">${escapeHtml(edu.institution)}</div>
          ${edu.description ? `<div class="item-description">${edu.description}</div>` : ''}
        </div>
      `).join('') : ''}

      ${cv.courses && cv.courses.length > 0 ? `
      <div class="section-title with-line" data-section-title="Courses & Certifications">Courses & Certifications</div>
      ` : ''}

      ${cv.courses && cv.courses.length > 0 ? cv.courses.map(course => `
        <div class="course-item">
          <div class="item-header">
            <div class="item-title">
              ${course.link ? `<a href="${escapeHtml(course.link)}">${escapeHtml(course.name)}</a>` : escapeHtml(course.name)}
              ${course.institution ? ` - <span class="item-subtitle-normal">${escapeHtml(course.institution)}</span>` : ''}
            </div>
            <div class="item-date">${escapeHtml(course.year)}</div>
          </div>
          ${course.description ? `<div class="item-description">${course.description}</div>` : ''}
        </div>
      `).join('') : ''}

      ${cv.skills && cv.skills.length > 0 ? `
      <div class="section-title with-line" data-section-title="Skills">Skills</div>
      <div data-section="skills">
        <ul class="skills-list">
          ${cv.skills.map(skillLine => {
            // Parse the skill line to make category bold
            const colonIndex = skillLine.indexOf(':')
            if (colonIndex > 0) {
              const category = skillLine.substring(0, colonIndex)
              const skills = skillLine.substring(colonIndex + 1)
              return `<li><span class="skill-category">${escapeHtml(category)}:</span>${escapeHtml(skills)}</li>`
            }
            return `<li>${escapeHtml(skillLine)}</li>`
          }).join('')}
        </ul>
      </div>
      ` : ''}
    `
  }

  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  const handleDownloadPDF = async () => {
    setIsDownloading(true)
    try {
      // Generate the HTML content as a string
      const styles = `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          line-height: 1;
          color: #000000;
          background: #ffffff;
          margin: 0;
          padding: 0;
        }
        .cv-container {
          width: 21.0cm;
          margin: 0 auto;
          background: white;
          box-sizing: border-box;
          padding: 0 2.54cm 2.54cm 2.54cm;
        }
        .cv-content-wrapper {
          padding-top: 1.6cm;
        }
        /* Ensure section titles maintain proper spacing after page breaks */
        .section-title {
          page-break-after: avoid;
        }
        .name {
          font-size: 16px;
          font-weight: bold;
          color: #000000;
          text-align: center;
          margin-bottom: 10px;
          text-transform: uppercase;
        }
        .contact-info {
          text-align: center;
          font-size: 14px;
          color: #000000;
          margin-bottom: 15px;
          line-height: 1.3;
        }
        .contact-info span {
          margin: 0 5px;
        }
        .cv-section {
          margin-bottom: 12px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .section-title {
          font-size: 14px;
          font-weight: bold;
          color: #000000;
          text-transform: uppercase;
          margin: 12px 0 8px 0;
          padding-bottom: 4px;
          letter-spacing: 1px;
        }
        .section-title.no-line {
          border-bottom: none;
        }
        .section-title.with-line {
          border-bottom: 2px solid #000000;
        }
        .section-content {
          font-size: 14px;
          line-height: 1.3;
          color: #000000;
        }
        .summary-content {
          font-size: 14px;
          line-height: 1;
          color: #000000;
          text-align: justify;
        }
        .experience-item,
        .project-item,
        .education-item,
        .course-item {
          margin-bottom: 10px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 3px;
        }
        .item-title {
          font-weight: bold;
          font-size: 14px;
          color: #000000;
        }
        .item-title-normal {
          font-weight: normal;
          font-size: 14px;
          color: #000000;
        }
        .item-title a,
        .item-title-normal a {
          color: #000000;
          text-decoration: none;
        }
        .item-date {
          font-size: 14px;
          color: #000000;
          white-space: nowrap;
        }
        .item-subtitle {
          font-size: 14px;
          font-style: italic;
          color: #000000;
          margin-bottom: 3px;
        }
        .item-subtitle-normal {
          font-size: 14px;
          font-style: normal;
          color: #000000;
          margin-bottom: 3px;
        }
        .item-description {
          font-size: 14px;
          line-height: 1.3;
          color: #000000;
          margin: 0;
          text-align: justify;
        }
        .item-description ul {
          margin: 5px 0;
          padding-left: 20px;
        }
        .item-description li {
          margin-bottom: 2px;
        }
        .skills-list {
          display: flex;
          flex-wrap: wrap;
          gap: 3px 10px;
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .skills-list li {
          font-size: 14px;
          color: #000000;
        }
        .skill-category {
          font-weight: bold;
          color: #000000;
        }
        @media print {
          @page {
            size: A4;
            margin: 1.6cm 2.54cm 2.54cm 2.54cm;
          }
          body {
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }
          .cv-container {
            box-shadow: none;
            margin: 0;
            padding: 0;
            width: 100%;
          }
          .cv-content-wrapper {
            padding-top: 0;
          }
          /* Ensure section titles don't get cut off by page breaks */
          .section-title {
            page-break-after: avoid;
            break-after: avoid;
          }
          .experience-item,
          .project-item,
          .education-item,
          .course-item {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `

      // Generate HTML as a single continuous document
      // Filter out empty pages first
      const filteredPages = pages
        .filter((pageSections, originalIndex) => {
          // Filter out pages that have no sections or empty sections
          if (pageSections.length === 0) {
            console.log(`Page ${originalIndex}: Empty (no sections)`)
            return false
          }

          // Check if page has any actual content (not just whitespace)
          const hasContent = pageSections.some(section => {
            const htmlWithoutTags = section.html.replace(/<[^>]*>/g, '').trim()
            return htmlWithoutTags.length > 0
          })

          if (!hasContent) {
            console.log(`Page ${originalIndex}: No content`)
          }

          return hasContent
        })

      console.log(`Total pages: ${pages.length}, Filtered pages: ${filteredPages.length}`)

      // Combine all content into a single document
      const allContent = filteredPages
        .map((pageSections, pageIndex) => {
          // Only include name and contact info on the first page
          const headerHtml = pageIndex === 0 ? `
            <div class="name">${escapeHtml(cv.personalInfo.name || 'YOUR NAME')}</div>
            <div class="contact-info">
              ${cv.personalInfo.email ? `<span>${escapeHtml(cv.personalInfo.email)}</span>` : ''}
              ${cv.personalInfo.phone ? `<span>| ${escapeHtml(cv.personalInfo.phone)}</span>` : ''}
              ${cv.personalInfo.linkedin ? `<span>| ${escapeHtml(cv.personalInfo.linkedin)}</span>` : ''}
              ${cv.personalInfo.portfolio ? `<span>| ${escapeHtml(cv.personalInfo.portfolio)}</span>` : ''}
            </div>
          ` : ''

          const sectionsHtml = pageSections.map((section) => section.html).join('')

          return headerHtml + sectionsHtml
        }).join('')

      // Wrap content with top padding
      const wrappedContent = `
        <div class="cv-content-wrapper">
          ${allContent}
        </div>
      `

      const fullHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>${escapeHtml(cv.personalInfo.name || 'CV')}</title>
          <style>${styles}</style>
        </head>
        <body>
          <div class="cv-container">
            ${wrappedContent}
          </div>
        </body>
        </html>
      `

      // Create hidden iframe and print directly
      const iframe = document.createElement('iframe')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '1px'
      iframe.style.height = '1px'
      iframe.style.border = 'none'
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
      if (iframeDoc) {
        iframeDoc.open()
        iframeDoc.write(fullHtml)
        iframeDoc.close()

        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      }

      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)
    } catch (err) {
      console.error('Failed to generate PDF:', err)
      alert('Failed to generate PDF. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div ref={containerRef} className="flex flex-col bg-gray-100" style={{ minHeight: '600px' }}>
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="text-sm text-gray-600">
          {cvName || cv.personalInfo.name || 'CV Preview'}
        </div>
        <div className="flex items-center gap-4">
          {onSave && (
            <Button
              onClick={onSave}
              disabled={isSaving}
              variant="default"
              size="sm"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save CV
                </>
              )}
            </Button>
          )}
          <Button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            variant="outline"
            size="sm"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </>
            )}
          </Button>
          <div className="text-xs text-gray-500">
            {pages.length} {pages.length === 1 ? 'Page' : 'Pages'} | Scale: {Math.round(scale * 100)}%
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      <div className="flex-1 overflow-auto bg-gray-200 p-6" style={{ maxHeight: 'calc(100vh - 140px)' }}>
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            display: 'inline-block'
          }}
        >
          {/* Hidden measurement div */}
          <div
            ref={measurementRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              width: `${A4_WIDTH - MARGIN_LEFT - MARGIN_RIGHT}px`,
              pointerEvents: 'none'
            }}
          >
            <style>{`
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: Arial, sans-serif;
                line-height: 1;
                color: #000000;
              }
              .name {
                font-size: 16px;
                font-weight: bold;
                text-align: center;
                margin-bottom: 10px;
                text-transform: uppercase;
              }
              .contact-info {
                text-align: center;
                font-size: 14px;
                margin-bottom: 15px;
                line-height: 1.3;
              }
              .contact-info span {
                margin: 0 5px;
              }
              .cv-section {
                margin-bottom: 12px;
              }
              .section-title {
                font-size: 14px;
                font-weight: bold;
                text-transform: uppercase;
                margin: 12px 0 8px 0;
                padding-bottom: 4px;
                letter-spacing: 1px;
              }
              .section-title.no-line {
                border-bottom: none;
              }
              .section-title.with-line {
                border-bottom: 2px solid #000000;
              }
              .section-content {
                font-size: 14px;
                line-height: 1.3;
              }
              .summary-content {
                font-size: 14px;
                line-height: 1;
                text-align: justify;
              }
              .experience-item,
              .project-item,
              .education-item,
              .course-item {
                margin-bottom: 10px;
              }
              .item-header {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                margin-bottom: 3px;
              }
              .item-title {
                font-weight: bold;
                font-size: 14px;
              }
              .item-title-normal {
                font-weight: normal;
                font-size: 14px;
              }
              .item-date {
                font-size: 14px;
                white-space: nowrap;
              }
              .item-subtitle {
                font-size: 14px;
                font-style: italic;
                margin-bottom: 3px;
              }
              .item-subtitle-normal {
                font-size: 14px;
                font-style: normal;
                margin-bottom: 3px;
              }
              .item-description {
                font-size: 14px;
                line-height: 1.3;
                margin: 0;
                text-align: justify;
              }
              .item-description ul {
                margin: 5px 0;
                padding-left: 20px;
              }
              .item-description li {
                margin-bottom: 2px;
              }
              .skills-list {
                display: flex;
                flex-wrap: wrap;
                gap: 3px 10px;
                margin: 0;
                padding: 0;
                list-style: none;
              }
              .skills-list li {
                font-size: 14px;
              }
              .skill-category {
                font-weight: bold;
              }
            `}</style>
            <div dangerouslySetInnerHTML={{ __html: generateHTMLContent() }} />
          </div>

          {/* Render pages */}
          {pages.map((pageSections, pageIndex) => (
            <div
              key={pageIndex}
              className="cv-page bg-white shadow-2xl mb-4"
              style={{
                width: `${A4_WIDTH}px`,
                height: `${A4_HEIGHT}px`,
                boxSizing: 'border-box',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Page content area with exact margins */}
              <div
                style={{
                  position: 'absolute',
                  top: `${MARGIN_TOP}px`,
                  left: `${MARGIN_LEFT}px`,
                  right: `${MARGIN_RIGHT}px`,
                  bottom: `${MARGIN_BOTTOM}px`,
                  overflow: 'hidden'
                }}
              >
                <style>{`
                  * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                  }
                  .cv-page-content {
                    font-family: Arial, sans-serif;
                    line-height: 1;
                    color: #000000;
                  }
                  .name {
                    font-size: 16px;
                    font-weight: bold;
                    text-align: center;
                    margin-bottom: 10px;
                    text-transform: uppercase;
                  }
                  .contact-info {
                    text-align: center;
                    font-size: 14px;
                    margin-bottom: 15px;
                    line-height: 1.3;
                  }
                  .contact-info span {
                    margin: 0 5px;
                  }
                  .cv-section {
                    margin-bottom: 12px;
                  }
                  .section-title {
                    font-size: 14px;
                    font-weight: bold;
                    text-transform: uppercase;
                    margin: 12px 0 8px 0;
                    padding-bottom: 4px;
                    letter-spacing: 1px;
                  }
                  .section-title.no-line {
                    border-bottom: none;
                  }
                  .section-title.with-line {
                    border-bottom: 2px solid #000000;
                  }
                  .section-content {
                    font-size: 14px;
                    line-height: 1.3;
                  }
                  .summary-content {
                    font-size: 14px;
                    line-height: 1;
                    text-align: justify;
                  }
                  .experience-item,
                  .project-item,
                  .education-item,
                  .course-item {
                    margin-bottom: 10px;
                  }
                  .item-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                    margin-bottom: 3px;
                  }
                  .item-title {
                    font-weight: bold;
                    font-size: 14px;
                  }
                  .item-title-normal {
                    font-weight: normal;
                    font-size: 14px;
                  }
                  .item-date {
                    font-size: 14px;
                    white-space: nowrap;
                  }
                  .item-subtitle {
                    font-size: 14px;
                    font-style: italic;
                    margin-bottom: 3px;
                  }
                  .item-subtitle-normal {
                    font-size: 14px;
                    font-style: normal;
                    margin-bottom: 3px;
                  }
                  .item-description {
                    font-size: 14px;
                    line-height: 1.3;
                    margin: 0;
                    text-align: justify;
                  }
                  .item-description ul {
                    margin: 5px 0;
                    padding-left: 20px;
                  }
                  .item-description li {
                    margin-bottom: 2px;
                  }
                  .skills-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 3px 10px;
                    margin: 0;
                    padding: 0;
                    list-style: none;
                  }
                  .skills-list li {
                    font-size: 14px;
                  }
                  .skill-category {
                    font-weight: bold;
                  }
                `}</style>
                <div className="cv-page-content">
                  {/* Name and contact only on first page */}
                  {pageIndex === 0 && (
                    <>
                      <div className="name">{escapeHtml(cv.personalInfo.name || 'YOUR NAME')}</div>
                      <div className="contact-info">
                        {cv.personalInfo.email && <span>{escapeHtml(cv.personalInfo.email)}</span>}
                        {cv.personalInfo.phone && <span>| {escapeHtml(cv.personalInfo.phone)}</span>}
                        {cv.personalInfo.linkedin && <span>| {escapeHtml(cv.personalInfo.linkedin)}</span>}
                        {cv.personalInfo.portfolio && <span>| {escapeHtml(cv.personalInfo.portfolio)}</span>}
                      </div>
                    </>
                  )}

                  {/* Sections for this page */}
                  {pageSections.map((section, sectionIndex) => (
                    <div
                      key={sectionIndex}
                      dangerouslySetInnerHTML={{ __html: section.html }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default HTMLCVPreview
